import { Parameter } from "domain/entities/parameter";
import { Project } from "domain/entities/project";
import { ProjectMember } from "domain/entities/project-member";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import { ProjectInputFilter } from "domain/models/project-input-filter";
import { ProjectMemberInputFilter } from "domain/models/project-member-input-filter";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputCurrencyComponent from "presentation/components/Shared/InputCurrencyComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import MultiSelectComponent from "presentation/components/Shared/MultiSelectComponent";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useProjectsByFilter } from "presentation/hooks/use-query-project";
import { useProjectMembersByFilter } from "presentation/hooks/use-query-project-member";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { usePurchaseOrderStore } from "presentation/stores/use-purchase-order-store";
import { useEffect, useState } from "react";

const PurchaseOrderInfoForm = () => {
  const { data: projectsData, refetch: refetchProjects } = useProjectsByFilter({
    limit: 100,
  } as ProjectInputFilter);
  const [selectedProjectId, setSelectedProjectId] = useState<string>();
  const { purchaseOrder, setPurchaseOrder } = usePurchaseOrderStore();
  const { data: projectMembersData, refetch: refetchProjectMember } =
    useProjectMembersByFilter({
      projectId: selectedProjectId,
      limit: 1000,
      sortBy: "employee.name",
      startRange: purchaseOrder?.startAt,
      endRange: purchaseOrder?.endAt,
    } as ProjectMemberInputFilter);
  const { purchaseOrderStatus, purchaseOrderType } = useParameterStore();
  const [projects, setProjects] = useState<Parameter[]>();
  const [currentMember, setCurrentMember] = useState();
  const [projectMembers, setProjectMembers] = useState<Parameter[]>();
  const [selectedEmployees, setSelectedEmployees] = useState<any>([]);

  const sortProjMembersByName = (ids: string[]): string[] => {
    const sortedIds = ids.sort((a, b) => {
      const nameA = projectMembers?.find((pm) => pm.id === a)?.value;
      const nameB = projectMembers?.find((pm) => pm.id === b)?.value;
      if (nameA && nameB) return nameA.localeCompare(nameB);
      return 0;
    });
    return sortedIds;
  };

  const { data: activeEmployees} = useEmployeesByFilter({limit: 100, isActive: true} as EmployeeInputFilter);

  useEffect(() => {
    const projectMembersDataActive = projectMembersData?.data?.filter((po) => activeEmployees?.data?.some(emp => emp.id === po.employee?.id));
    const formatedProjectMembers = projectMembersDataActive?.map((item) => {
      const employeeName = item.employee?.name;
      const start = item.startAt
        ? new Date(item.startAt).toISOString().split("T")[0]
        : "";
      const end = item.endAt
        ? new Date(item.endAt).toISOString().split("T")[0]
        : "";

      const employee: Parameter = {
        id: item.id,
        value: employeeName ? `${employeeName} (${start} <> ${end})` : "",
        type: "projectMember",
      };

      return employee;
    });
    setProjectMembers(formatedProjectMembers);
  }, [projectMembersData, activeEmployees]);

  useEffect(() => {
    const formatedProjects = projectsData?.data?.map((item) => {
      const project: Parameter = {
        id: item.id,
        value: item.name,
        type: "project",
      };

      return project;
    });
    setProjects(formatedProjects);
  }, [projectsData]);

  useEffect(() => {
    if (purchaseOrder?.type) {
      if (
        purchaseOrder.type === "Serviço" ||
        purchaseOrder.type === "Interno" ||
        purchaseOrder.type === "Hora Extra" 
      ) {
        purchaseOrder.fixedValue = null;
      } else {
        purchaseOrder.fixedValue = 0;
      }
      setPurchaseOrder(purchaseOrder);
    }
  }, [purchaseOrder?.type]);

  useEffect(() => {
    if (
      purchaseOrder?.projectMembers &&
      purchaseOrder?.projectMembers?.length > 0
    ) {
      const firstMember = purchaseOrder.projectMembers[0];
      const projectId = firstMember.project?.id;
      if (projectId) {
        setSelectedProjectId(projectId);
      }
    }
  }, [purchaseOrder?.projectMembers]);

  useEffect(() => {
    if (
      purchaseOrder?.projectMembers &&
      purchaseOrder?.projectMembers?.length > 0 &&
      projectMembers &&
      projectMembers?.length > 0
    ) {
      const matchedIds = purchaseOrder.projectMembers
        .map((pm) => {
          const match = projectMembers.find((p) => p.id === pm.id);
          return match?.id;
        })
        .filter((id): id is string => Boolean(id));

      setSelectedEmployees(sortProjMembersByName(matchedIds));
    }
  }, [projectMembers]);

  useEffect(() => {
    if (purchaseOrder && selectedEmployees.length > 0) {
      const newProjectMembers = selectedEmployees.map((id: string) => ({ id }));

      const updatedPO = {
        ...purchaseOrder,
        projectMembers: newProjectMembers,
      };

      setPurchaseOrder(updatedPO);
    }
  }, [selectedEmployees]);

  useEffect(() => {
    if (purchaseOrder && purchaseOrder?.attachment !== null) {
      purchaseOrder.attachment = null;
      purchaseOrder.projectMembers = [];
      setPurchaseOrder({ ...purchaseOrder });
    }
  }, []);

  return (
    <div className="default-grid ">
      <div className="default-1-col">
        <InputTextComponent
          label="Nome"
          fieldName="name"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.name}
          setValue={setPurchaseOrder}
          isDisable={!!purchaseOrder?.id}
        />
      </div>
      <div className="default-1-col">
        <InputTextComponent
          label="Número"
          fieldName="poNumber"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.poNumber}
          setValue={setPurchaseOrder}
          isDisable={!!purchaseOrder?.id}
        />
      </div>
      <div className="default-1-col">
        <DropdownComponent
          options={purchaseOrderType}
          label="Tipo"
          fieldName="type"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.type}
          setValue={setPurchaseOrder}
          isDisable={!!purchaseOrder?.id}
        />
      </div>
      <div className="default-1-col">
        <DropdownComponent
          options={projects ?? []}
          label="Projeto"
          fieldName="project"
          currentObject={{}}
          getValue={selectedProjectId}
          setValue={(updatedObj) => {
            setSelectedProjectId(updatedObj.project.id);
          }}
          isValueId={true}
          isDisable={!!purchaseOrder?.id}
        />
      </div>
      <div className="default-1-col">
        <InputCurrencyComponent
          label="Valor da Ordem de Compra"
          fieldName="plafound"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.plafound}
          setValue={setPurchaseOrder}
          isDisable={!!purchaseOrder?.id}
        />
      </div>

      <div className="default-1-col">
        <InputCurrencyComponent
          label="Valor fixo diário"
          fieldName="fixedValue"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.fixedValue}
          setValue={setPurchaseOrder}
          isDisable={
            purchaseOrder?.type === "Serviço" ||
            purchaseOrder?.type === "Interno" ||
            purchaseOrder?.type === "Hora Extra" ||
            !!purchaseOrder?.id
          }
        />
      </div>
      <div className="default-1-col">
        <InputCurrencyComponent
          label="Saldo da Ordem de Compra"
          fieldName="plafoundLeft"
          isDisable={true}
          currentObject={purchaseOrder}
          getValue={
            purchaseOrder?.id
              ? purchaseOrder?.plafoundLeft
              : purchaseOrder?.plafound
          }
          setValue={setPurchaseOrder}
        />
      </div>
      <div className="default-1-col">
        <DropdownComponent
          options={purchaseOrderStatus}
          label="Status"
          fieldName="status"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.status}
          setValue={setPurchaseOrder}
        />
      </div>
      <div className="default-1-col">
        <InputDateComponent
          label="Data de início"
          fieldName="startAt"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.startAt}
          setValue={setPurchaseOrder}
        />
      </div>
      <div className="default-1-col">
        <InputDateComponent
          label="Data de término"
          fieldName="endAt"
          currentObject={purchaseOrder}
          getValue={purchaseOrder?.endAt}
          setValue={setPurchaseOrder}
        />
      </div>
      <div className="default-2-cols">
        <MultiSelectComponent
          options={projectMembers ?? []}
          label="Colaboradores"
          isValueId={true}
          fieldName="projectMember"
          currentObject={currentMember}
          getValue={selectedEmployees}
          setValue={(ids) => {
            const sorted = sortProjMembersByName(ids);
            setSelectedEmployees(sorted);
          }}
          isDisable={!selectedProjectId}
        />
      </div>
    </div>
  );
};

export default PurchaseOrderInfoForm;
